import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Shield, Heart, Leaf } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { getProducts, getCategories, seedData } from "@/lib/api";

const benefits = [
  { icon: Leaf, title: "100% Natural", desc: "Ingredientes de origen natural, sin químicos artificiales" },
  { icon: Shield, title: "Calidad Certificada", desc: "Productos avalados por Vida Divina a nivel mundial" },
  { icon: Heart, title: "Bienestar Integral", desc: "Soluciones para tu salud física y mental" },
  { icon: Star, title: "Resultados Reales", desc: "Miles de testimonios de clientes satisfechos" },
];

export default function HomePage() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        await seedData();
      } catch (e) { /* seed already done */ }
      try {
        const [prodRes, catRes] = await Promise.all([
          getProducts({ featured: true }),
          getCategories(),
        ]);
        setFeaturedProducts(prodRes.data);
        setCategories(catRes.data);
      } catch (e) {
        console.error("Error loading data:", e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="hero-pattern py-24 md:py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="flex flex-col items-start gap-4 animate-fade-in">
                <img src="/logo-oymer-real.png" alt="Oymer - Vida Divina" className="h-28 md:h-40 lg:h-48 w-auto object-contain" />
                <div className="rounded-full overflow-hidden shadow-[0_20px_60px_rgba(42,74,60,0.12)] w-48 h-48 md:w-64 md:h-64 lg:w-72 lg:h-72 border-4 border-white/60 -mt-6 md:-mt-8">
                  <img
                    src="https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/ths4agzh_40478.jpg"
                    alt="Oymer - Distribuidor Autorizado"
                    className="w-full h-full object-cover object-[center_20%]"
                  />
                </div>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl tracking-tighter font-medium text-forest leading-[0.95] animate-fade-in-up">
                Bienestar natural<br />
                <span className="text-terracotta">para tu vida</span>
              </h1>
              <p className="font-body text-base md:text-lg leading-relaxed text-sage max-w-lg animate-fade-in-up stagger-2">
                Descubre la línea completa de productos Vida Divina. Tés, cafés funcionales, suplementos y cuidado personal — todo con ingredientes 100% naturales.
              </p>
              <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-3">
                <Link
                  to="/catalogo"
                  data-testid="hero-cta-catalog"
                  className="bg-forest text-white rounded-full px-8 py-4 font-body font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(42,74,60,0.15)] inline-flex items-center gap-2"
                >
                  Explorar Catálogo <ArrowRight size={18} />
                </Link>
                <Link
                  to="/contacto"
                  data-testid="hero-cta-contact"
                  className="bg-white text-forest rounded-full px-8 py-4 font-body font-medium border border-[#E5E0D8] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  Hacer Pedido
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 relative animate-fade-in stagger-4">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(42,74,60,0.12)]">
                <img
                  src="https://images.unsplash.com/photo-1548626995-d16b6a752c97?w=800&q=80"
                  alt="Bienestar natural"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white/80 backdrop-blur-xl rounded-2xl p-4 shadow-lg border border-white/40 hidden lg:block">
                <p className="font-heading text-2xl font-bold text-forest">15+</p>
                <p className="font-body text-xs text-sage">Productos naturales</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="benefits-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-${i}`}
                className="space-y-3 opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.15}s`, animationFillMode: 'forwards' }}
              >
                <div className="w-12 h-12 rounded-2xl bg-forest/5 flex items-center justify-center">
                  <b.icon size={22} className="text-forest" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-forest">{b.title}</h3>
                <p className="font-body text-sm text-sage leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section data-testid="categories-section" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Categorías</span>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest mt-2">
                Nuestras líneas
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  to={`/catalogo?categoria=${cat.slug}`}
                  data-testid={`category-card-${cat.slug}`}
                  className="relative rounded-[2rem] overflow-hidden aspect-[3/4] group cursor-pointer opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.1}s`, animationFillMode: 'forwards' }}
                >
                  <img
                    src={cat.image_url}
                    alt={cat.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <h3 className="font-heading text-xl font-semibold text-white">{cat.name}</h3>
                    <p className="font-body text-sm text-white/70 mt-1">{cat.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {!loading && featuredProducts.length > 0 && (
        <section data-testid="featured-products-section" className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Destacados</span>
                <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest mt-2">
                  Productos populares
                </h2>
              </div>
              <Link
                to="/catalogo"
                data-testid="view-all-products-link"
                className="inline-flex items-center gap-2 font-body text-sm font-medium text-forest hover:text-terracotta transition-colors duration-200"
              >
                Ver todo el catálogo <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {featuredProducts.slice(0, 8).map((product, i) => (
                <ProductCard key={product.id} product={product} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonios Reales - Carrusel */}
      <section data-testid="testimonials-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Resultados Reales</span>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest mt-2">
              Testimonios de nuestros clientes
            </h2>
            <p className="font-body text-base md:text-lg text-sage mt-3 max-w-xl">
              Transformaciones reales de personas que confiaron en los productos Vida Divina.
            </p>
          </div>

          <Carousel opts={{ align: "start", loop: true }} plugins={[Autoplay({ delay: 3500, stopOnInteraction: false, stopOnMouseEnter: true })]} className="w-full">
            <CarouselContent className="-ml-4">
              {[
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/gahirm0q_40489.jpg", name: "Besy Lopez", result: "-12 lbs en 14 días", program: "Reto HCG Revolution" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/37sz8c5d_40482.jpg", name: "Cliente Vida Divina", result: "Resultados reales", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/ujbds4z9_40481.jpg", name: "Romina Trujillo", result: "-18.2 lbs en 14 días", program: "Reto HCG Revolution" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/mg8cocwi_40490.jpg", name: "Cliente Vida Divina", result: "Resultados reales", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/axbums6l_40495.jpg", name: "Cliente Vida Divina", result: "Transformación increíble", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/sk9ekohe_40477.jpg", name: "Cliente Vida Divina", result: "Transformación visible", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/e2f12fc7_40496.jpg", name: "Cliente Vida Divina", result: "Antes y Después", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/583vcdgl_40485.jpg", name: "Cliente Vida Divina", result: "Cambio total de vida", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/kfjlb3kc_40497.jpg", name: "Meliza Noriega", result: "171 a 160 lbs", program: "Reto HCG 2025 - TeDivina" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/amgoqg9j_40487.jpg", name: "Cliente Vida Divina", result: "Pérdida de peso notable", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/o3xralov_50803.jpg", name: "Cliente Vida Divina", result: "Transformación total", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/u0e25o9e_50806.jpg", name: "Cliente Vida Divina", result: "Cambio de vida real", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/qa2e28nd_50815.jpg", name: "Cliente Vida Divina", result: "Transformación asombrosa", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/mle8xkpx_50827.jpg", name: "Cliente Vida Divina", result: "Resultados increíbles", program: "Antes y Después" },
                { img: "https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/72lyaxbp_50824.jpg", name: "Cliente Vida Divina", result: "Cambio notable", program: "Antes y Después" },
              ].map((t, i) => (
                <CarouselItem key={i} className="pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                  <div
                    data-testid={`testimonial-card-${i}`}
                    className="bg-white rounded-[2rem] overflow-hidden border border-[#E5E0D8] shadow-[0_8px_30px_rgb(42,74,60,0.04)] hover:shadow-[0_20px_40px_rgb(42,74,60,0.08)] transition-all duration-500 hover:-translate-y-2 group h-full"
                  >
                    <div className="overflow-hidden">
                      <img
                        src={t.img}
                        alt={`Testimonio de ${t.name}`}
                        className="w-full aspect-[4/3] object-cover object-top product-card-img"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6">
                      <p className="font-heading text-lg font-semibold text-forest">{t.name}</p>
                      <p className="font-body text-base font-bold text-terracotta mt-1">{t.result}</p>
                      <p className="font-body text-xs text-sage mt-1">{t.program}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center justify-center gap-4 mt-8">
              <CarouselPrevious className="static translate-y-0 bg-forest text-white hover:bg-forest-dark border-none w-10 h-10 rounded-full" />
              <CarouselNext className="static translate-y-0 bg-forest text-white hover:bg-forest-dark border-none w-10 h-10 rounded-full" />
            </div>
          </Carousel>

          <p className="font-body text-xs text-sage mt-8 max-w-2xl">
            * Los resultados individuales pueden variar. Estos testimonios son de clientes reales de Vida Divina. Los resultados dependen de factores como el peso inicial, tipo de cuerpo, ejercicio y dieta.
          </p>
        </div>
      </section>

      {/* About / CTA */}
      <section data-testid="about-section" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="rounded-[2rem] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=800&q=80"
                alt="Sobre Vida Divina"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div className="space-y-6">
              <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Sobre Nosotros</span>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest">
                Tu bienestar es nuestra misión
              </h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-sage">
                Soy Oymer, distribuidor autorizado de Vida Divina en México. Mi compromiso es brindarte acceso a los mejores productos naturales que transformarán tu salud y calidad de vida.
              </p>
              <p className="font-body text-base leading-relaxed text-sage">
                Vida Divina opera en más de 58 países, ofreciendo productos formulados con ingredientes naturales como el Ganoderma (Reishi), hierbas ancestrales y superalimentos que han demostrado resultados reales.
              </p>
              <Link
                to="/contacto"
                data-testid="about-cta-button"
                className="inline-flex bg-terracotta text-white rounded-full px-8 py-4 font-body font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:bg-terracotta-dark items-center gap-2"
              >
                Contáctame <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
